import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Filter, MarketDetailsResponse, MarketsService, RetailersService } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

interface RetailerOption {
  id: number;
  text: string;
}

@Component({
  selector: 'app-manage-market-retailers',
  templateUrl: './manage-market-retailers.component.html',
  styleUrl: './manage-market-retailers.component.scss',
  host: {
    class: 'app-manage-market-retailers'
  },
  standalone: false
})
export class ManageMarketRetailersComponent extends BaseViewComponent implements OnInit, OnDestroy {

  private readonly destroyedComponent$ = new Subject<void>();
  private retailersById = new Map<number, RetailerOption>();

  marketId: number = 0;
  market: MarketDetailsResponse | undefined;

  retailersList: RetailerOption[] = [];
  assignedRetailers: RetailerOption[] = [];
  originalRetailerIds: number[] = [];

  retailersTotalRecords: number = 0;
  retailersRowsPerPageOption: number = 10;

  isSaving: boolean = false;
  isLoadingMarket: boolean = true;

  public manageMarketRetailersFormGroup: FormGroup = this.fb.group({
    retailers: [[]]
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private marketsService: MarketsService,
    private retailersService: RetailersService,
    private fb: FormBuilder
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(async params => {
        this.marketId = Number(params['marketId']);
        this.market = undefined;
        this.retailersById.clear();
        this.originalRetailerIds = [];
        this.assignedRetailers = [];
        this.manageMarketRetailersFormGroup.controls['retailers'].setValue([]);

        await this.get();

        if (this.market) {
          await this.getRetailers({
            pageIndex: 1,
            search: ''
          });
        }
      });
  }

  override async get(): Promise<void> {
    this.isLoadingMarket = true;
    this.market = undefined;

    try {
      if (!this.marketId) {
        return;
      }

      this.market = await firstValueFrom(this.marketsService.apiMarketsDetailsIdGet(this.marketId));

      this.setAssignedRetailers(this.market.retailers ?? []);
    } catch (error) {
      console.error('Error fetching market data:', error);
    } finally {
      this.isLoadingMarket = false;
    }
  }

  private setAssignedRetailers(retailers: MarketDetailsResponse['retailers']): void {

    this.assignedRetailers = (retailers ?? []).map(retailer => ({
      id: retailer.id,
      text: retailer.code ? `${retailer.name} (${retailer.code})` : retailer.name
    }));

    this.originalRetailerIds = this.assignedRetailers.map(
      retailer => retailer.id
    );

    this.manageMarketRetailersFormGroup.controls['retailers'].setValue([]);
  }

  onLazyLoadRetailers(event: any): void {
    this.getRetailers(event);
  }

  async getRetailers(event: any): Promise<void> {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, event?.pageIndex ?? 1);
    const pageSize = Number(event?.pageSize) || this.retailersRowsPerPageOption;

    this.retailersRowsPerPageOption = pageSize;

    const additionalFilters: Filter[] = [
      {
        propertyName: 'isActive',
        operator: FilterOperator.EQUALS,
        values: ['true']
      }
    ];

    // exclude already assigned retailers
    if (this.originalRetailerIds.length > 0) {
      additionalFilters.push({
        propertyName: 'id',
        operator: FilterOperator.NOT_IN,
        values: this.originalRetailerIds.map(
          retailerId => String(retailerId)
        )
      });
    }

    const filter = {
      textFilter: search,
      additionalFilters,
      pageNumber: activePage,
      pageSize: this.retailersRowsPerPageOption
    };

    try {
      const retailersPage = await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter));

      const retailers: RetailerOption[] = [];

      for (const retailer of retailersPage.results ?? []) {

        // map resp to combo format
        if (retailer.id != null && retailer.text != null) {
          retailers.push({
            id: retailer.id,
            text: retailer.text
          });
        }
      }

      this.retailersList = retailers;

      this.retailersList.forEach(retailer => {
        this.retailersById.set(retailer.id, retailer);
      });

      this.retailersTotalRecords = retailersPage.totalCount ?? 0;
    } catch (error) {
      console.error('Error fetching retailers:', error);
      this.retailersList = [];
      this.retailersTotalRecords = 0;
    }
  }

  getRetailersTotalPages(): number {
    return Math.max(
      1,
      Math.ceil(this.retailersTotalRecords / this.retailersRowsPerPageOption)
    );
  }

  public getSelectedRetailers(): RetailerOption[] {
    const selectedRetailerIds = this.manageMarketRetailersFormGroup.controls['retailers'].value ?? [];

    const selectedRetailers: RetailerOption[] = [];

    for (const retailerId of selectedRetailerIds) {
      const retailer = this.retailersById.get(Number(retailerId));

      if (retailer) {
        selectedRetailers.push(retailer);
      }
    }

    return selectedRetailers;
  }

  async submitForm(): Promise<void> {
    if (
      this.manageMarketRetailersFormGroup.invalid ||
      this.isSaving ||
      this.isLoadingMarket ||
      !this.market
    ) {
      return;
    }

    const formRetailerIds: number[] = this.manageMarketRetailersFormGroup.controls['retailers'].value ?? [];

    const retailerIdsToAdd = [
      ...new Set(
        formRetailerIds.filter(
          retailerId => !this.originalRetailerIds.includes(retailerId)
        )
      )
    ];

    if (retailerIdsToAdd.length === 0) {
      return;
    }

    const addRequest = {
      retailerIds: retailerIdsToAdd
    };

    this.isSaving = true;
    this.manageMarketRetailersFormGroup.disable();

    try {
      await firstValueFrom(this.marketsService.apiMarketsIdRetailersAddPut(this.marketId, addRequest));

      await this.router.navigate(['/markets', this.marketId]);
    } catch (error) {
      console.error('Error adding market retailers:', error);
      await this.get();
    } finally {
      this.isSaving = false;
      this.manageMarketRetailersFormGroup.enable();
    }
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
